import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";

export type PmUser = {
  id: number | null;
  name: string | null;
  email: string | null;
  saveBilling: boolean | null;
  hasSubscription: boolean | null;
  hasSentLetter: boolean | null;
  hasSentEmessage: boolean | null;
  hasPurchasedCredits: boolean | null;
  hasPaidInvoice: boolean | null;
  previousSubscription: unknown;
  tosAgreement: unknown;
  creditBalance: number;
  messageCreditsAvailable: boolean;
  messageCreditBalance: number;
  totalMessageCredits: number;
  sentLetterTotal: number | null;
  raw: any;
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const toBool = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  return fallback;
};

export const normalizeUserFromApi = (rawInput: any): PmUser => {
  const raw = rawInput && rawInput.user ? rawInput.user : rawInput;

  const messageCreditBalance = Math.max(
    0,
    toNumber(raw?.messageCreditBalance ?? raw?.message_credit_balance),
  );

  return {
    id: typeof raw?.id === "number" ? raw.id : raw?.id ? Number(raw.id) : null,
    name: typeof raw?.name === "string" ? raw.name : null,
    email: typeof raw?.email === "string" ? raw.email : null,
    saveBilling:
      raw?.saveBilling ?? raw?.save_billing ?? null,
    hasSubscription:
      raw?.hasSubscription ?? raw?.has_subscription ?? null,
    hasSentLetter:
      raw?.hasSentLetter ?? raw?.has_sent_letter ?? raw?.has_sent_letters ?? null,
    hasSentEmessage:
      raw?.hasSentEmessage ?? raw?.has_sent_emessage ?? null,
    hasPurchasedCredits:
      raw?.hasPurchasedCredits ?? raw?.has_purchased_credits ?? null,
    hasPaidInvoice:
      raw?.hasPaidInvoice ?? raw?.has_paid_invoice ?? null,
    previousSubscription: raw?.previousSubscription ?? raw?.previous_subscription ?? null,
    tosAgreement: raw?.tosAgreement ?? raw?.tos_agreement ?? null,
    creditBalance: Math.max(0, toNumber(raw?.creditBalance ?? raw?.credit_balance)),
    messageCreditsAvailable: toBool(
      raw?.messageCreditsAvailable ?? raw?.message_credits_available,
      false,
    ),
    messageCreditBalance,
    totalMessageCredits: Math.max(0, toNumber(raw?.totalMessageCredits ?? raw?.total_message_credits)),
    sentLetterTotal:
      raw?.sentLetterTotal != null || raw?.sent_letter_total != null
        ? toNumber(raw?.sentLetterTotal ?? raw?.sent_letter_total)
        : null,
    raw,
  };
};

export async function fetchAuthenticatedUser(): Promise<PmUser> {
  const raw = await pmFetchJson<any>("/validate-token", { method: "GET" });
  return normalizeUserFromApi(raw);
}
